import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useGraph } from '../../../store/simulationStore';

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  className?: string;
}

export const TagInput = ({ value, onChange, placeholder = 'Add tags...', className = '' }: TagInputProps) => {
  const graph = useGraph();
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  // Get all existing tags from all nodes for autocomplete
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    graph.nodes.forEach((node) => {
      if (node.data.tags) {
        node.data.tags.forEach((tag) => {
          // Normalize tags (lowercase, trim)
          const normalized = tag.toLowerCase().trim();
          if (normalized) {
            tagSet.add(normalized);
          }
        });
      }
    });
    return Array.from(tagSet).sort();
  }, [graph.nodes]);

  // Filter suggestions based on input
  const suggestions = useMemo(() => {
    if (!inputValue.trim()) {
      return allTags.slice(0, 10); // Show top 10 when no input
    }
    const lowerInput = inputValue.toLowerCase().trim();
    return allTags
      .filter((tag) => tag.includes(lowerInput) && !value.includes(tag))
      .slice(0, 10);
  }, [inputValue, allTags, value]);

  // Check if current input would be a new tag
  const isNewTag = useMemo(() => {
    if (!inputValue.trim()) return false;
    const normalized = inputValue.toLowerCase().trim();
    return !allTags.includes(normalized) && !value.includes(normalized);
  }, [inputValue, allTags, value]);

  // Update dropdown position
  const updatePosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4, // 4px gap
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // Update position on focus, input change, and window resize/scroll
  useEffect(() => {
    if (isFocused) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true); // Capture phase for all scrollable parents
      
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [isFocused, inputValue, updatePosition]);

  const addTag = useCallback(
    (tag: string) => {
      const normalized = tag.toLowerCase().trim();
      if (normalized && !value.includes(normalized)) {
        onChange([...value, normalized]);
        setInputValue('');
        inputRef.current?.focus();
      }
    },
    [value, onChange]
  );

  const removeTag = useCallback(
    (tagToRemove: string) => {
      onChange(value.filter((tag) => tag !== tagToRemove));
    },
    [value, onChange]
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && inputValue.trim()) {
        e.preventDefault();
        addTag(inputValue);
      } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
        e.preventDefault();
        removeTag(value[value.length - 1]);
      } else if (e.key === 'Escape') {
        setInputValue('');
        inputRef.current?.blur();
      }
    },
    [inputValue, value, addTag, removeTag]
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  return (
    <div ref={containerRef} className={`tag-input-container ${className}`}>
      <div className="tag-input-wrapper">
        {value.map((tag) => (
          <span key={tag} className="tag-badge">
            {tag}
            <button
              type="button"
              className="tag-remove"
              onClick={() => removeTag(tag)}
              aria-label={`Remove tag ${tag}`}
            >
              <X size={14} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="tag-input"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            // Delay to allow clicking on suggestions
            setTimeout(() => setIsFocused(false), 200);
          }}
          placeholder={value.length === 0 ? placeholder : ''}
        />
      </div>
      {isFocused && (suggestions.length > 0 || isNewTag) && dropdownPosition && createPortal(
        <div 
          className="tag-suggestions"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 99999, // Very high z-index to ensure it's above everything
            maxHeight: '200px',
            overflowY: 'auto',
            right: 'auto',
            marginTop: 0,
          }}
        >
          {suggestions.map((tag) => (
            <button
              key={tag}
              type="button"
              className="tag-suggestion-item"
              onClick={() => addTag(tag)}
              onMouseDown={(e) => e.preventDefault()} // Prevent input blur
            >
              {tag}
            </button>
          ))}
          {isNewTag && (
            <div className="tag-suggestion-item tag-suggestion-new">
              <span>{inputValue.trim()}</span>
              <span className="tag-suggestion-hint">Press Enter to add</span>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};


